import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  TableContainer,
  Box,
  Table,
  Typography,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
} from "@mui/material";
import { Container, Stack } from "@mui/system";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

// Services
import { useLazyGetUserAuthTokenQuery } from "services/auth-service";
import { useLazyGetDomainDataQuery, useUpdateCancelledStatusMutation, useLazyGetDomainEmailInvoiceQuery, useLazyGetPartnerAndSubuserListQuery } from "services/dfy-service";

// Hooks
import { useOrders } from "./hooks/useOrders";

// Components
import OrderTableRow from "./components/orderTableRow";
import OrderTableHeader from "./components/orderTableHeader";
import SearchFilters from "./components/searchFilters";
import PaginationFooter from "./components/paginationFooter";
import { TableLoadingState, TableEmptyState } from "./components/tableStates";
import DynamicSelect from "./components/dynamicSelect";

/**
 * Escape CSV value - wrap in quotes if contains comma, quote, or newline
 */
const escapeCSVValue = (value) => {
  const stringValue = String(value ?? "");
  if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

/**
 * Convert array of objects to CSV string and trigger download
 */
const downloadCSV = (data, filename) => {
  if (!data || data.length === 0) return;

  // Get headers from first object keys
  const headers = Object.keys(data[0]);

  // Create CSV content
  const csvContent = [
    headers.join(","), // Header row
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header] ?? "";
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          const stringValue = String(value);
          if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(",")
    ),
  ].join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Get month name from month number
 */
const getMonthName = (monthNumber) => {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return months[monthNumber - 1] || monthNumber;
};

/**
 * Download invoice CSV with header summary and order breakdown
 */
const downloadInvoiceCSV = (invoiceData, partnerData, filename) => {
  const rows = [];

  // Invoice Summary Header
  rows.push(["Invoice Number", "Invoice Month", "Invoice Year", "Partner User", "Grand Total Amount (USD)"]);

  // Invoice Summary Data
  rows.push([
    escapeCSVValue(invoiceData.invoiceNumber || "---"),
    escapeCSVValue(getMonthName(invoiceData.month)),
    escapeCSVValue(invoiceData.year || "---"),
    escapeCSVValue(partnerData.partnerName || partnerData.partnerEmail || "---"),
    escapeCSVValue(`$${invoiceData.grandTotalAmount || 0}`)
  ]);

  // Empty row for separation
  rows.push([]);

  // Order Breakdown Header
  rows.push(["User Email", "Total Domains", "Total Mailboxes", "Amount (USD)"]);

  // Order Breakdown Data
  (partnerData.orders || []).forEach((order) => {
    rows.push([
      escapeCSVValue(order.userEmail || "---"),
      escapeCSVValue(order.domainsCount || 0),
      escapeCSVValue(order.mailboxCount || 0),
      escapeCSVValue(`$${order.amount || 0}`)
    ]);
  });

  // Convert rows to CSV string
  const csvContent = rows.map(row => row.join(",")).join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * White Label DFY Orders Page
 * Displays and manages order status with filtering, pagination, and CSV export
 */
const WhiteLabelDFY = () => {
  const navigate = useNavigate();

  // Auth token query
  const [getToken, { data: tokenData }] = useLazyGetUserAuthTokenQuery();

  // Orders query for CSV export
  const [getDomainDataForCSV] = useLazyGetDomainDataQuery();
  const [getInvoicesForCSV] = useLazyGetDomainEmailInvoiceQuery();

  // Partner list for export modal
  const [
    fetchPartnerList,
    { data: partnerListData }
  ] = useLazyGetPartnerAndSubuserListQuery();

  // Local state
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [exportModal, setExportModal] = useState({
    open: false,
    partnerId: "",
    month: "",
    year: "",
  });

  // Custom hook for orders data management
  const {
    orders,
    totalCount,
    isLoading,
    searchText,
    status,
    partnerId,
    subUserId,
    sortBy,
    sortOrder,
    page,
    rowsPerPage,
    handleSearchChange,
    handleStatusChange,
    handlePartnerChange,
    handleSubUserChange,
    handleSortChange,
    handlePageChange,
    handleRowsPerPageChange,
    updateOrder,
  } = useOrders();

  // Cancelled status mutation
  const [updateCancelledStatus] = useUpdateCancelledStatusMutation();

  // View order handler - navigate to order details page
  const handleViewOrder = useCallback(
    (order) => {
      navigate(`/manage/whiteLabel-DFY/${order._id}`);
    },
    [navigate]
  );

  // Export CSV handler - Fetches domain data (same as View Order) and exports to CSV
  const handleExportCSV = useCallback(async (order) => {
    try {
      const response = await getDomainDataForCSV({
        orderId: order._id,
        status: "",
        query: "",
        page: 1,
        limit: 10000,
      }).unwrap();

      const domains = response?.data?.domains || [];
      const csvRows = [];

      domains.forEach((domain) => {
        (domain.contactDetails || []).forEach((contact) => {
          csvRows.push({
            "Email": order.userEmail || "-",
            "Domain Name": domain.domain || "-",
            "Forward Domain": domain.forwardDomain || "-",
            "Provider": domain.provider || "-",
            "First Name": contact.firstName || "-",
            "Last Name": contact.lastName || "-",
            "Mailbox Email": contact.email || "-",
            "Status": contact.status || "-",
            "Purchase Date": domain.createdAt || domain.startDate
              ? new Date(domain.createdAt || domain.startDate).toLocaleDateString()
              : "-",
            "partnerEmail": order.partnerEmail || "-",
          });
        });
      });

      if (csvRows.length > 0) {
        downloadCSV(csvRows, `${order.userEmail || order.user}_orders.csv`);
      }
    } catch (error) {
      console.error("Failed to export CSV:", error);
    }
  }, [getDomainDataForCSV]);

  // Partner options for export modal
  const exportPartnerOptions = useMemo(() => {
    if (!partnerListData?.data) return [];
    return partnerListData.data.map((item) => {
      const { partner } = item;
      const name = `${partner.name?.first || ""} ${partner.name?.last || ""}`.trim();
      return {
        value: partner._id,
        label: name || partner.email || partner._id,
      };
    });
  }, [partnerListData]);

  // Month options for export modal (exclude current & future months for current year)
  const allMonths = useMemo(() => [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ], []);

  const monthOptions = useMemo(() => {
    const selectedYear = Number(exportModal.year);
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // 1-indexed

    // For current year, only show past months (before current month)
    if (selectedYear === currentYear) {
      return allMonths.filter((m) => Number(m.value) < currentMonth);
    }
    // For past years, show all months
    return allMonths;
  }, [exportModal.year, allMonths]);

  // Year options for export modal
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 3; y--) {
      years.push({ value: String(y), label: String(y) });
    }
    return years;
  }, []);

  // Open export modal
  const handleOpenExportModal = useCallback(() => {
    fetchPartnerList();
    setExportModal({ open: true, partnerId: "", month: "", year: "" });
  }, [fetchPartnerList]);

  // Close export modal
  const handleCloseExportModal = useCallback(() => {
    setExportModal({ open: false, partnerId: "", month: "", year: "" });
  }, []);

  // Export from modal handler
  const handleExportFromModal = useCallback(async () => {
    const { partnerId: modalPartnerId, month, year } = exportModal;
    if (!modalPartnerId || !month || !year) return;

    setIsExportingAll(true);
    try {
      const response = await getInvoicesForCSV({ partnerId: modalPartnerId, month, year }).unwrap();
      const invoices = response?.data || [];

      if (invoices.length > 0) {
        const invoice = invoices[0];
        const partnerData = invoice.partners?.find(p => p.partnerId === modalPartnerId) || invoice.partners?.[0];

        if (partnerData) {
          const filename = `invoice_${invoice.invoiceNumber || modalPartnerId}_${new Date().toISOString().split("T")[0]}.csv`;
          downloadInvoiceCSV(invoice, partnerData, filename);
          handleCloseExportModal();
        } else {
          toast.error("No partner data found in the invoice");
        }
      } else {
        toast.error("No invoice data found for the selected criteria");
      }
    } catch (error) {
      console.error("Failed to export invoices CSV:", error);
      toast.error("Failed to export invoices data");
    } finally {
      setIsExportingAll(false);
    }
  }, [exportModal, getInvoicesForCSV, handleCloseExportModal]);

  // Manage account handler
  const handleManageAccount = useCallback(
    async (userId) => {
      setIsRedirecting(true);
      await getToken({ id: userId });
    },
    [getToken]
  );

  // Confirmation dialog state for cancelled from DFY
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    orderId: null,
    cancelledFromDFY: null,
  });

  // Handle cancelled from DFY - show confirmation first
  const handleCancelledChangeRequest = useCallback((orderId, cancelledFromDFY) => {
    setConfirmDialog({ open: true, orderId, cancelledFromDFY });
  }, []);

  const handleConfirmCancelledChange = useCallback(async () => {
    const { orderId, cancelledFromDFY } = confirmDialog;
    setConfirmDialog({ open: false, orderId: null, cancelledFromDFY: null });

    updateOrder(orderId, { cancelledFromDFY });
    try {
      await updateCancelledStatus({ orderId, cancelledFromDFY });
      toast.success("Cancelled from DFY status updated successfully");
    } catch (error) {
      console.error("Failed to update cancelled status:", error);
      toast.error("Failed to update cancelled status");
    }
  }, [confirmDialog, updateOrder, updateCancelledStatus]);

  const handleCancelDialog = useCallback(() => {
    setConfirmDialog({ open: false, orderId: null, cancelledFromDFY: null });
  }, []);

  // Handle auth token redirect
  useEffect(() => {
    if (tokenData?.authToken && isRedirecting) {
      setIsRedirecting(false);
      window.open(
        `${process.env.REACT_APP_FRONTEND_URL}/verify?token=${tokenData.authToken}&skipUpdate=true`,
        "_blank"
      );
    }
  }, [tokenData, isRedirecting]);

  // Render table body content
  const renderTableBody = () => {
    if (isLoading) {
      return <TableLoadingState colSpan={8} />;
    }

    if (orders.length === 0) {
      return <TableEmptyState colSpan={8} />;
    }

    return (
      <TableBody>
        {orders.map((order, index) => (
          <OrderTableRow
            key={`${order._id}_index_${index}`}
            order={order}
            onViewOrder={handleViewOrder}
            onExportCSV={handleExportCSV}
            onManageAccount={handleManageAccount}
            onCancelledChange={handleCancelledChangeRequest}
          />
        ))}
      </TableBody>
    );
  };

  return (
    <>
      <title>Order Status</title>

      <Box component="main" sx={{ flexGrow: 1, py: 2 }}>
        <Container maxWidth="xl">
          <Stack spacing={3}>
            {/* Page Header */}
            <Stack direction="row" justifyContent="space-between" spacing={4}>
              <Stack spacing={1}>
                <Typography variant="h4">Order Status</Typography>
              </Stack>
            </Stack>

            {/* Search and Filters */}
            <SearchFilters
              searchText={searchText}
              status={status}
              partnerId={partnerId}
              subUserId={subUserId}
              onSearchChange={handleSearchChange}
              onStatusChange={handleStatusChange}
              onPartnerChange={handlePartnerChange}
              onSubUserChange={handleSubUserChange}
              onExportAllCSV={handleOpenExportModal}
            />

            {/* Orders Table */}
            <TableContainer
              sx={{
                borderRadius: "0px",
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  width: "14px",
                  height: "14px",
                },
                "&::-webkit-scrollbar-track": {
                  borderRadius: "60px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#E4E4E5",
                  borderRadius: "10px",
                  border: "4px solid rgba(0, 0, 0, 0)",
                  backgroundClip: "padding-box",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#d5d5d5",
                },
              }}
            >
              <Table
                sx={{
                  height: "fit-content",
                  minWidth: "1200px",
                  borderCollapse: "separate",
                  borderSpacing: "0px 15px !important",
                }}
                aria-label="Orders table"
                stickyHeader
              >
                <OrderTableHeader
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSortChange={handleSortChange}
                />
                {renderTableBody()}
              </Table>
            </TableContainer>

            {/* Pagination */}
            <PaginationFooter
              totalCount={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              isLoading={isLoading}
            />
          </Stack>
        </Container>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCancelDialog}>
        <DialogTitle>Confirm Status Update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to change the &ldquo;Cancelled from DFY&rdquo; status to &ldquo;{confirmDialog.cancelledFromDFY}&rdquo;?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleCancelDialog}>Cancel</MuiButton>
          <MuiButton onClick={handleConfirmCancelledChange} variant="contained" color="primary">
            Confirm
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Export Invoice Modal */}
      <Dialog open={exportModal.open} onClose={handleCloseExportModal} maxWidth="sm" fullWidth>
        <DialogTitle>Export Invoice Data</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <DynamicSelect
              label="Select Partner"
              selectValue={exportModal.partnerId}
              onSortChange={(val) => setExportModal((prev) => ({ ...prev, partnerId: val }))}
              options={exportPartnerOptions}
              minWidth={200}
            />
            <DynamicSelect
              label="Select Month"
              selectValue={exportModal.month}
              onSortChange={(val) => setExportModal((prev) => ({ ...prev, month: val }))}
              options={monthOptions}
              minWidth={200}
            />
            <DynamicSelect
              label="Select Year"
              selectValue={exportModal.year}
              onSortChange={(val) => {
                const currentYear = new Date().getFullYear();
                const currentMonth = new Date().getMonth() + 1;
                setExportModal((prev) => {
                  // Reset month if it becomes invalid for the new year
                  const monthStillValid = Number(val) !== currentYear || Number(prev.month) < currentMonth;
                  return { ...prev, year: val, month: monthStillValid ? prev.month : "" };
                });
              }}
              options={yearOptions}
              minWidth={200}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleCloseExportModal}>Cancel</MuiButton>
          <MuiButton
            onClick={handleExportFromModal}
            variant="contained"
            color="primary"
            disabled={!exportModal.partnerId || !exportModal.month || !exportModal.year || isExportingAll}
          >
            {isExportingAll ? "Exporting..." : "Export"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WhiteLabelDFY;